import React, { Suspense } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
// Landing pages stay eagerly imported — they're the initial paint.
import PublicMapPage from '@/components/pages/PublicMapPage'
import LoginPage from '@/components/pages/LoginPage'
// Everything else is code-split so the initial bundle stays small.
const DashboardPage = React.lazy(() => import('@/components/pages/DashboardPage'))
const EntityPage = React.lazy(() => import('@/components/pages/EntityPage'))
const ForgotPasswordPage = React.lazy(() => import('@/components/pages/ForgotPasswordPage'))
const ListPage = React.lazy(() => import('@/components/pages/ListPage'))
const MyDocumentsPage = React.lazy(() => import('@/components/pages/MyDocumentsPage'))
const RegisterPage = React.lazy(() => import('@/components/pages/RegisterPage'))
const ReportSuccessPage = React.lazy(() => import('@/components/pages/ReportSuccessPage'))
const ResetPasswordPage = React.lazy(() => import('@/components/pages/ResetPasswordPage'))
const LogViewerPage = React.lazy(() => import('@/components/pages/LogViewerPage'))
const UserManualPage = React.lazy(() => import('@/components/pages/UserManualPage'))
const UserDetailPage = React.lazy(() => import('@/components/pages/UserDetailPage'))
const UserAdminPage = React.lazy(() => import('@/components/pages/UserAdminPage'))
const MunicipalAssetsPage = React.lazy(() => import('@/components/pages/MunicipalAssetsPage'))
const PriorityBulkPage = React.lazy(() => import('@/components/pages/PriorityBulkPage'))
import { useAuth } from '@/contexts/AuthContext'
import PrivateLayout from '@/layouts/PrivateLayout'
import PublicLayout from '@/layouts/PublicLayout'
import ExternalRedirect from '@/routes/ExternalRedirect'
import { Me } from '@/services/AuthService'
import TokenHandler from './TokenHandler'

// Minimal fallback while a lazy chunk loads.
const RouteFallback = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh' }}>
    <div style={{ opacity: 0.6 }}>…</div>
  </div>
)

export default function AppRoutes() {
  const { me } = useAuth()
  type EntityFromMe = {
    entityType: 'organization' | 'municipality' | 'partner'
    entityId: string
  } | null

  function getEntityFromMe(me?: Me | null): EntityFromMe {
    if (!me) {
      return null
    }

    if (me.organizationId) {
      return { entityType: 'organization', entityId: String(me.organizationId) }
    }
    if (me.municipalityId) {
      return { entityType: 'municipality', entityId: String(me.municipalityId) }
    }
    const pid = (me as any).partnerId ?? (me as any).partnerID // cover both spellings if needed
    if (pid) {
      return { entityType: 'partner', entityId: String(pid) }
    }
    return null
  }

  return (
    <Suspense fallback={<RouteFallback />}>
    <Routes>
      <Route path="/token/:token/redirect" element={<TokenHandler />} />

      {/* Public - Map is the main landing page */}
      <Route path="/" element={<PublicMapPage />} />
      <Route path="/report/success" element={<ReportSuccessPage />} />

      {/* Auth pages with public layout */}
      <Route element={<PublicLayout />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register/:id_register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
      </Route>

      {/* Private */}
      <Route element={<PrivateLayout />}>
        <Route path="/home" element={<DashboardPage />} />

        <Route
          path="/my-entity"
          element={(() => {
            const ent = getEntityFromMe(me)
            if (!me) {
              return <Navigate to="/login" replace />
            }
            if (!ent) {
              return <div>No entity linked to your account</div>
            }
            return (
              <Navigate
                replace
                to={`/entity/${encodeURIComponent(ent.entityType)}/${encodeURIComponent(ent.entityId)}`}
                state={{ self: true }}
              />
            )
          })()}
        />
        <Route path="/entities/:entityType" element={<ListPage />} />
        <Route path="/entities/:entityType/all" element={<ListPage />} />
        <Route path="/entities/:entityType/contracted" element={<ListPage />} />
        <Route path="/entity/:entityType/:entityId/" element={<EntityPage />} />
        <Route path="/entity/:entityType/:entityId/invites" element={<ListPage />} />
        <Route path="/entity/:entityType/:entityId/all" element={<EntityPage />} />
        <Route path="/me" element={<UserDetailPage showDetails />} />
        <Route path="/me/:userId" element={<UserDetailPage showDetails />} />
        <Route path="/my-documents" element={<MyDocumentsPage />} />
        <Route path="/admin/logs" element={<LogViewerPage />} />
        <Route path="/admin/users" element={<UserAdminPage />} />
        <Route path="/admin/municipal-assets" element={<MunicipalAssetsPage />} />
        <Route path="/admin/priority-bulk" element={<PriorityBulkPage />} />
        <Route path="/user-manual" element={<UserManualPage />} />

        {/*/!* Global lists *!/*/}
        {/*<Route path="/global/organizations" element={<ListPage />} />*/}
        {/*<Route path="/global/municipalities" element={<ListPage />} />*/}
        {/*<Route path="/global/partners" element={<ListPage />} />*/}

        {/* Scoped lists */}
        {/*<Route*/}
        {/*  path="/organizations/:id_organizations/municipalities"*/}
        {/*  element={<ListPage />}*/}
        {/*/>*/}
        {/*<Route path="/organizations/:id_organizations/partners" element={<ListPage />} />*/}
        {/*<Route path="/municipalities" element={<ListPage />} />*/}
        {/*<Route path="/partners" element={<ListPage />} />*/}

        {/* Details */}
        {/*<Route*/}
        {/*  path="/organizations/:id_organizations/details"*/}
        {/*  element={<DetailsPage />}*/}
        {/*/>*/}
        {/*<Route*/}
        {/*  path="/municipalities/:id_municipalities/details"*/}
        {/*  element={<DetailsPage />}*/}
        {/*/>*/}
        {/*<Route path="/partners/:id_partners/details" element={<DetailsPage />} />*/}
      </Route>

      {/* Fallback */}
      <Route
        path="*"
        element={<ExternalRedirect to={import.meta.env.VITE_FALLBACK_NO_PROJECT_URL} />}
      />
    </Routes>
    </Suspense>
  )
}
